// ============================================
// CAREER PAGE
// ============================================

import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Compass, Target, TrendingUp, Award, Clock, CheckCircle2, Circle,
  ChevronRight, Loader2, Ship, Anchor, AlertTriangle, BookOpen,
  BarChart3, ArrowRight, Sparkles,
} from 'lucide-react';
import {
  useCareerPaths, useMyCareerProgress, useMyNextSteps, useRanks,
  useSetCareerPath, useMarketInsights,
} from '@/hooks';
import { cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/career')({
  component: CareerPage,
});

function CareerPage() {
  const { t } = useTranslation(['panel', 'common', 'enums']);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [showPathSelector, setShowPathSelector] = useState(false);

  const { data: progressResponse, isLoading: progressLoading } = useMyCareerProgress();
  const { data: nextStepsResponse, isLoading: stepsLoading } = useMyNextSteps();
  const { data: pathsResponse, isLoading: pathsLoading } = useCareerPaths(selectedDepartment || undefined);
  const { data: ranksResponse } = useRanks(selectedDepartment || undefined);
  const { data: insightsResponse } = useMarketInsights(selectedDepartment || undefined);
  const setCareerPathMutation = useSetCareerPath();

  const progress: any = (progressResponse as any)?.data || progressResponse || null;
  const nextSteps: any[] = (nextStepsResponse as any)?.data || nextStepsResponse || [];
  const paths: any[] = (pathsResponse as any)?.data || pathsResponse || [];
  const ranks: any[] = (ranksResponse as any)?.data || ranksResponse || [];
  const insights: any = (insightsResponse as any)?.data || insightsResponse || null;

  const departments = ['DECK', 'ENGINE', 'ELECTRICAL', 'CATERING'];

  const handleSetCareerPath = async (careerPathId: string, currentRankId: string) => {
    try {
      await setCareerPathMutation.mutateAsync({ careerPathId, currentRankId });
      setShowPathSelector(false);
    } catch (err) {
      console.error('Failed to set career path:', err);
    }
  };

  if (progressLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-maritime-600" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('panel:career.title')}</h1>
          <p className="text-gray-500 mt-1">{t('panel:career.subtitle')}</p>
        </div>
        {progress?.currentPath && (
          <button onClick={() => setShowPathSelector(true)} className="btn-secondary">
            <Compass className="w-4 h-4" />{t('panel:career.changePath')}
          </button>
        )}
      </div>

      {/* No Career Path Selected */}
      {!progress?.currentPath && !showPathSelector && (
        <div className="card p-8 text-center">
          <div className="w-16 h-16 mx-auto bg-maritime-100 rounded-full flex items-center justify-center mb-4">
            <Compass className="w-8 h-8 text-maritime-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('panel:career.selectPath')}</h2>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">{t('panel:career.selectPathDescription')}</p>
          <button onClick={() => setShowPathSelector(true)} className="btn-primary">
            <Target className="w-4 h-4" />{t('panel:career.selectPathButton')}
          </button>
        </div>
      )}

      {/* Career Path Selector */}
      {showPathSelector && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">{t('panel:career.selectPath')}</h2>
            {progress?.currentPath && (
              <button onClick={() => setShowPathSelector(false)} className="text-sm text-gray-500 hover:text-gray-700">
                {t('common:cancel')}
              </button>
            )}
          </div>
          
          {/* Department Filter */}
          <div className="p-4 border-b border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('panel:career.department')}</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedDepartment('')}
                className={cn(
                  'px-3 py-1.5 rounded-maritime text-sm font-medium transition-colors',
                  !selectedDepartment ? 'bg-maritime-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {t('common:all')}
              </button>
              {departments.map((key) => (
                <button
                  key={key}
                  onClick={() => setSelectedDepartment(key)}
                  className={cn(
                    'px-3 py-1.5 rounded-maritime text-sm font-medium transition-colors',
                    selectedDepartment === key ? 'bg-maritime-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  )}
                >
                  {t(`enums:department.${key}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Paths Grid */}
          <div className="p-4">
            {pathsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-maritime-600" />
              </div>
            ) : paths.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {t('panel:career.noPathsFound')}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {paths.map((path: any) => (
                  <div
                    key={path.id}
                    className={cn(
                      'border rounded-maritime p-4 cursor-pointer transition-all hover:shadow-md',
                      progress?.currentPath?.id === path.id
                        ? 'border-maritime-500 bg-maritime-50'
                        : 'border-gray-200 hover:border-maritime-300'
                    )}
                    onClick={() => {
                      const startRank = ranks.find((r: any) => r.department === path.department);
                      if (startRank) handleSetCareerPath(path.id, startRank.id);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-maritime bg-maritime-100 flex items-center justify-center flex-shrink-0">
                        <Anchor className="w-5 h-5 text-maritime-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{path.name}</h3>
                        <p className="text-sm text-gray-500 mt-1">{path.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="badge badge-gray text-xs">
                            {t(`enums:department.${path.department}`, { defaultValue: path.department })}
                          </span>
                          {path.steps?.length > 0 && (
                            <span className="text-xs text-gray-400">
                              {t('panel:career.stepsCount', { count: path.steps.length })}
                            </span>
                          )}
                        </div>
                      </div>
                      {progress?.currentPath?.id === path.id && (
                        <CheckCircle2 className="w-5 h-5 text-maritime-600 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Progress Section */}
      {progress?.currentPath && !showPathSelector && (
        <>
          {/* Progress Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-maritime bg-blue-100 flex items-center justify-center">
                  <Ship className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('panel:career.currentRank')}</p>
                  <p className="font-semibold text-gray-900">{progress.currentRank?.name || '-'}</p>
                </div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-maritime bg-green-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('panel:career.seaService')}</p>
                  <p className="font-semibold text-gray-900">{t('panel:career.daysCount', { count: progress.totalSeaServiceDays || 0 })}</p>
                </div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-maritime bg-purple-100 flex items-center justify-center">
                  <Award className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('panel:career.certificates')}</p>
                  <p className="font-semibold text-gray-900">{t('panel:career.certificateCount', { count: progress.certificateCount || 0 })}</p>
                </div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-maritime bg-amber-100 flex items-center justify-center">
                  <Target className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('panel:career.targetRank')}</p>
                  <p className="font-semibold text-gray-900">{progress.nextRank?.name || t('panel:career.completed')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{t('panel:career.progress')}</h2>
              <span className="text-2xl font-bold text-maritime-600">
                %{progress.progressPercentage || 0}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
              <div
                className="bg-gradient-to-r from-maritime-500 to-maritime-600 h-4 rounded-full transition-all duration-500"
                style={{ width: `${progress.progressPercentage || 0}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>{progress.currentRank?.name || t('panel:career.start')}</span>
              <span>{t('panel:career.stepsCompleted', { completed: progress.completedSteps || 0, total: progress.totalSteps || 0 })}</span>
              <span>{progress.nextRank?.name || t('panel:career.target')}</span>
            </div>
          </div>

          {/* Next Steps */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900">{t('panel:career.nextSteps')}</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {stepsLoading ? (
                  <div className="p-6 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-maritime-600" />
                  </div>
                ) : nextSteps.length === 0 ? (
                  <div className="p-6 text-center">
                    <Sparkles className="w-12 h-12 mx-auto text-green-500 mb-2" />
                    <p className="text-gray-600">{t('panel:career.allStepsCompleted')}</p>
                  </div>
                ) : (
                  nextSteps.slice(0, 3).map((step: any, index: number) => (
                    <div key={step.rank?.id || index} className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                          index === 0 ? 'bg-maritime-100 text-maritime-600' : 'bg-gray-100 text-gray-400'
                        )}>
                          {index === 0 ? <ArrowRight className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">{step.rank?.name}</h3>
                          
                          {/* Sea Service Progress */}
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                              <span>{t('panel:career.seaService')}</span>
                              <span>
                                {step.requirements?.currentSeaServiceDays || 0} / {step.requirements?.minSeaServiceDays || 0} {t('panel:career.days')}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-green-500 h-1.5 rounded-full"
                                style={{ width: `${Math.min(100, step.requirements?.seaServiceProgress || 0)}%` }}
                              />
                            </div>
                          </div>

                          {/* Required Certificates */}
                          {step.requirements?.requiredCertificates?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-500 mb-1">{t('panel:career.requiredCertificates')}:</p>
                              <div className="flex flex-wrap gap-1">
                                {step.requirements.requiredCertificates.slice(0, 3).map((cert: any) => (
                                  <span
                                    key={cert.id}
                                    className={cn('badge text-xs', cert.hasCertificate ? 'badge-success' : 'badge-warning')}
                                  >
                                    {cert.hasCertificate && <CheckCircle2 className="w-3 h-3 mr-1" />}
                                    {cert.name}
                                  </span>
                                ))}
                                {step.requirements.requiredCertificates.length > 3 && (
                                  <span className="text-xs text-gray-400">
                                    +{step.requirements.requiredCertificates.length - 3} {t('panel:career.more')}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Estimated Time */}
                          {step.estimatedTimeToReach && (
                            <p className="text-xs text-gray-400 mt-2">
                              {t('panel:career.estimatedTime', { months: Math.round(step.estimatedTimeToReach / 30) })}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {nextSteps.length > 0 && (
                <div className="p-4 border-t border-gray-200">
                  <Link to="/profile/certificates" className="text-sm text-maritime-600 hover:text-maritime-700 flex items-center gap-1">
                    {t('panel:career.manageCertificates')} <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </div>

            {/* Recommendations & Insights */}
            <div className="space-y-6">
              {nextSteps[0]?.recommendations?.length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-maritime-600" />{t('panel:career.recommendations')}
                    </h2>
                  </div>
                  <div className="p-4 space-y-3">
                    {nextSteps[0].recommendations.map((rec: string, index: number) => (
                      <div key={index} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-maritime-500 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-gray-600">{rec}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Market Insights */}
              {insights && (
                <div className="card">
                  <div className="card-header">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-maritime-600" />{t('panel:career.marketInsights')}
                    </h2>
                  </div>
                  <div className="p-4 space-y-4">
                    {insights.averageSalary && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{t('panel:career.averageSalary')}</span>
                        <span className="font-semibold text-gray-900">${insights.averageSalary?.toLocaleString()}</span>
                      </div>
                    )}
                    {insights.jobOpenings !== undefined && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{t('panel:career.openPositions')}</span>
                        <span className="font-semibold text-gray-900">{insights.jobOpenings}</span>
                      </div>
                    )}
                    {insights.demandTrend && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{t('panel:career.demandTrend')}</span>
                        <span className={cn(
                          'badge',
                          insights.demandTrend === 'increasing' ? 'badge-success' :
                          insights.demandTrend === 'decreasing' ? 'badge-error' : 'badge-warning'
                        )}>
                          <TrendingUp className={cn('w-3 h-3 mr-1', insights.demandTrend === 'decreasing' && 'rotate-180')} />
                          {t(`panel:career.trend.${insights.demandTrend}`)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Missing Certificates Warning */}
              {nextSteps[0]?.requirements?.requiredCertificates?.some((c: any) => !c.hasCertificate) && (
                <div className="card border-amber-200 bg-amber-50/50">
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-medium text-amber-800">{t('panel:career.missingCertificates')}</h3>
                        <p className="text-sm text-amber-700 mt-1">{t('panel:career.missingCertificatesDesc')}</p>
                        <Link
                          to="/profile/certificates"
                          className="text-sm text-amber-800 font-medium hover:text-amber-900 mt-2 inline-flex items-center gap-1"
                        >
                          {t('panel:career.addCertificate')} <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
